import { useCallback, useMemo } from "react";
import { MultiPolygon, Polygon } from "@turf/helpers";
import { InfoPopover } from "react-migration/components/InfoPopover";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { useAccountQuery } from "react-migration/domains/dashboard/topup/hooks/useAccountQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { TitleWithOwnerNames } from "react-migration/domains/ownership/typings/Title";
import { useSelectedTitle } from "../../hooks/useSelectedTitle";
import { LoadingPanel } from "../LoadingPanel";
import { Summary } from "../Summary";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { Banner } from "react-migration/components/Banner";

interface UKOwnershipDetailPageProps {
  selection: SelectionFeature;
  selectedTitleNumber?: string;
  setDetailSelection?(selection?: SelectionFeature): void;
  hasUnlimitedOwnershipClicks: boolean;
}

export const UKOwnershipDetailPage = ({
  selection,
  selectedTitleNumber,
  setDetailSelection,
  hasUnlimitedOwnershipClicks,
}: UKOwnershipDetailPageProps) => {
  const { selectedTitle, loadingSelectedTitle } = useSelectedTitle(selectedTitleNumber);
  const { data } = useAccountQuery();
  const location = selection.feature!.geometry as Polygon | MultiPolygon;

  const ownershipViewsLimitReached = useMemo(
    () =>
      !hasUnlimitedOwnershipClicks &&
      !!data &&
      data.accountWithViews.ownership_views.cap <= data.accountWithViews.ownership_views.used,
    [data, hasUnlimitedOwnershipClicks]
  );

  const setSelectedOwner = useCallback(
    (name?: string) => {
      setDetailSelection?.(
        name
          ? {
              id: name,
              type: SelectionType.OWNER,
              feature: selection.feature,
              titleNumber: selectedTitleNumber,
            }
          : undefined
      );
      logEventV2({
        name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
        properties: {
          cardName: "Ownership",
          action: "goTo",
          clickDetail: "Title owner",
        },
      });
    },
    [selectedTitleNumber, selection.feature, setDetailSelection]
  );

  if (loadingSelectedTitle) {
    return <LoadingPanel />;
  }

  return (
    selectedTitle && (
      <TitleDetailLayout
        selectedTitle={selectedTitle}
        location={location}
        setSelectedOwner={setSelectedOwner}
        ownershipViewsLimitReached={ownershipViewsLimitReached}
      />
    )
  );
};

interface TitleDetailLayoutProps {
  selectedTitle: TitleWithOwnerNames;
  location: GeoJSON.MultiPolygon | GeoJSON.Polygon | GeoJSON.Point;
  setSelectedOwner(ownerName?: string): void;
  ownershipViewsLimitReached: boolean;
}

const TitleDetailLayout = ({
  selectedTitle,
  location,
  setSelectedOwner,
  ownershipViewsLimitReached,
}: TitleDetailLayoutProps) => {
  return (
    <>
      {ownershipViewsLimitReached && <OwnershipViewsLimitBanner />}
      <Summary geometry={location} setSelectedOwner={setSelectedOwner} selectedTitle={selectedTitle} />
    </>
  );
};

function OwnershipViewsLimitBanner() {
  const { t } = useTranslation();
  return (
    <Banner.Informative className="border-solid py-[2px] mx-2">
      <div className="flex justify-between items-center">
        <span className="text-xs text-neutral-600 font-normal">
          {t("sidebar.ownership_sidebar.reached_ownership_viewing_limit")}
        </span>
        <InfoPopover size="xs">
          {t("sidebar.ownership_sidebar.reached_ownership_viewing_limit_explanation")}
        </InfoPopover>
      </div>
    </Banner.Informative>
  );
}
